import { z } from "zod";
import { Label } from "components/label";
import { Controller, useForm } from "react-hook-form";
import { HttpError, useCreate, useOne, useTranslation } from "@refinedev/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { SwitchButton } from "components/switchButton";
import Button from "components/button";
import { Link } from "react-router-dom";
import Select from "components/select";
import { get } from "lodash";
import { IEInvoiceSetting, IEInvoiceSettingForm } from "interfaces";
import Textarea from "components/textarea";
import Input from "components/input";
import { FullScreenLoading } from "components/fullScreenLoading";
import { ConfirmModal } from "components/confirmModal";
import { useState } from "react";
import toast from "react-hot-toast";

export interface IEInvoiceSettingFormProps {
  onSubmit: (data: IEInvoiceSettingForm) => void;
  defaultValues: IEInvoiceSetting;
}

const EditEInvoiceSettingFormSchema = z.object({
  einvoice_enabled: z.boolean(),
  sepay_username: z.string().min(1, "This field is required"),
  sepay_password: z.string().min(1, "This field is required"),
});

const INITIAL_ACTION_MODAL_CONFIG = {
  open: false,
  submit: () => {},
};

export const WPSEditEInvoiceSettingForm = ({
  onSubmit,
  defaultValues,
}: IEInvoiceSettingFormProps) => {
  const { mutateAsync: postMutationAsync, isLoading: isLoading } = useCreate();
  const { translate } = useTranslation();
  const [confirmModalConfig, setConfirmModalConfig] = useState(
    INITIAL_ACTION_MODAL_CONFIG,
  );

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<IEInvoiceSettingForm, HttpError, IEInvoiceSettingForm>({
    defaultValues: {
      einvoice_enabled: defaultValues?.einvoice_enabled ?? false,
      sepay_password: "",
      sepay_username: defaultValues?.sepay_username ?? "",
    },
    resolver: zodResolver(EditEInvoiceSettingFormSchema),
  });

  const _submit = (values: IEInvoiceSettingForm) => {
    if (!values.einvoice_enabled && defaultValues.einvoice_enabled) {
      setConfirmModalConfig({
        open: true,
        submit: () => _updateEInvoiceSetting(values),
      });
    } else {
      _updateEInvoiceSetting(values);
    }
  };

  const _updateEInvoiceSetting = async (values: IEInvoiceSettingForm) => {
    const submitValues = { ...values };
    const isConnected = await _testConnection(
      submitValues?.sepay_username,
      submitValues?.sepay_password,
    );
    if (isConnected) {
      onSubmit(submitValues);
    }
  };

  const _testConnection = async (username: string, password: string) => {
    try {
      const data = await postMutationAsync({
        resource: "organization/einvoice-settings/test",
        invalidates: [],
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          username,
          password,
        },
      });

      const isSuccess = get(data, "data.data.success", false);
      if (isSuccess) {
        return true;
      } else {
        const message = get(data, "data.data.message", "");
        toast.error(message);
        return false;
      }
    } catch (error) {
      return false;
    }
  };

  const _onCloseConfirmModal = () => {
    setConfirmModalConfig(INITIAL_ACTION_MODAL_CONFIG);
  };

  return (
    <div className="w-full p-4">
      <form
        className="flex flex-col gap-y-2 sm:gap-y-4"
        onSubmit={handleSubmit(_submit)}
      >
        <div className="w-full box-container">
          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
            <div className="sm:col-span-2">
              <Label required id="einvoice_enabled" name="Einvoice Enabled" />
              <Controller
                control={control}
                name="einvoice_enabled"
                defaultValue={true}
                render={({ field: { onChange, value = true, ref } }) => (
                  <SwitchButton checked={value} onChange={onChange} />
                )}
              />
              {errors["einvoice_enabled"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["einvoice_enabled"]?.message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-2">
              <Label required id="sepay_username" name="Username" />
              <Input
                id="sepay_username"
                type="text"
                placeholder="Username"
                error={errors["sepay_username"]}
                {...register("sepay_username")}
              />
              {errors["sepay_username"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors?.sepay_username.message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-2">
              <Label
                required
                id="sepay_password"
                name={translate("password")}
              />
              <Input
                id="sepay_password"
                type="password"
                placeholder={translate("password")}
                error={errors["sepay_password"]}
                {...register("sepay_password")}
              />
              {errors["sepay_password"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors?.sepay_password.message}
                  </span>
                </p>
              ) : null}
            </div>
          </div>
        </div>
        <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto">
          <div className="flex items-center justify-end gap-x-4">
            <Link to="/organization/einvoice">
              <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                {translate("button.cancel")}
              </Button>
            </Link>
            <Button className="w-20" type="submit">
              {translate("button.save")}
            </Button>
          </div>
        </div>
      </form>
      {isLoading && <FullScreenLoading />}
      <ConfirmModal
        key="confirm"
        title={translate("modal.toggleStatus", {
          status: translate("deactivate"),
          object: translate("einvoice"),
        })}
        content={translate("deactivate-einvoice-note")}
        open={confirmModalConfig.open}
        onConfirm={confirmModalConfig.submit}
        onClose={_onCloseConfirmModal}
      />
    </div>
  );
};
